// Mirrors the web Settings page's information architecture (Settings
// Categories → drill into a category). Same category list as
// src/app/settings/page.tsx; each drills into a native editor. Dash has no
// settings on web either (placeholder); everything else is a real editor.
import React, { useEffect, useState } from 'react';
import { getRevealEnabled, setRevealEnabled } from './revealPref';
import { useThemeMode, useThemePref, type ThemePref } from '../v10/theme';
import { Alert, Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './settings/ThemedSwitch';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from '../v10/theme';
import { Atmos, Chip, GlassCard, SectionTitle, Field, GoldButton } from '../v10/ui';
import { useAuth } from '../auth/AuthProvider';
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { scheduleTestReminder } from '../lib/notifications';
import { useSidebarConfig } from './useSidebarConfig';
import { OrderedConfigEditor } from './settings/OrderedConfigEditor';
import {
  BOTTOM_NAV_CATALOG,
  DEFAULT_BOTTOM_NAV,
  MAX_BOTTOM_NAV,
  bottomNavLabel,
} from './dashboardConfig';
import { iconForTool } from './toolIcons';
import GroupsEditor from './settings/GroupsEditor';
import ValuesEditor from './settings/ValuesEditor';
import EtcIntervalsEditor from './settings/EtcIntervalsEditor';
import CalendarEditor from './settings/CalendarEditor';
import JournalTypesEditor from './settings/JournalTypesEditor';
import ProjectsEditor from './settings/ProjectsEditor';
import TkneEditor from './settings/TkneEditor';
import PomodoroEditor from './settings/PomodoroEditor';
import MetisEditor from './settings/MetisEditor';
import ArgosEditor from './settings/ArgosEditor';
import HrmezEditor from './settings/HrmezEditor';
import OracleEditor from './settings/OracleEditor';
import OlmpsSettingsEditor from './settings/OlmpsSettingsEditor';
import SyqiDashboardEditor from './settings/SyqiDashboardEditor';
import DriftRulesEditor from './settings/DriftRulesEditor';
import TrashEditor from './settings/TrashEditor';

interface SettingCategory {
  id: string;
  title: string;
  description: string;
}

// Same id/title/description as settingsCategories in src/app/settings/page.tsx.
const CATEGORIES: SettingCategory[] = [
  { id: 'generalConfig', title: 'General', description: 'Sidebar, appearance, and default screen.' },
  { id: 'syqiConfig', title: 'SyQi', description: 'Choose which tools appear in each ORDER section of the SyQi dashboard.' },
  { id: 'olmpsConfig', title: 'ORACLE', description: 'Change your prescribed future — sector Endeavours, deities, and aims.' },
  { id: 'trashConfig', title: 'Trash & Archive', description: 'Restore or permanently delete trashed items.' },
  { id: 'oracleConfig', title: 'ASTRA', description: 'Auto-spin and rotation speed.' },
  { id: 'taskConfig', title: 'Task Configuration', description: 'Manage task groups, values, and ETC intervals.' },
  { id: 'calendarConfig', title: 'Calendar Configuration', description: 'Customize calendar views and event defaults.' },
  { id: 'journalConfig', title: 'Journal Configuration', description: 'Customize journal entry types and prompts.' },
  { id: 'projectsConfig', title: 'Projects Configuration', description: 'Customize project settings, forms, and labels.' },
  { id: 'designConfig', title: 'TKNE', description: 'Manage ORDER groups and basic project templates.' },
  { id: 'dashConfig', title: 'Dash', description: 'Configure dashboard and stats views.' },
  { id: 'hrmezConfig', title: 'H.R.M.E.Z', description: 'Pad classification icons, file routing, Google Drive, and AI settings.' },
  { id: 'argosConfig', title: 'A.R.G.O.S', description: 'Browser activity tracking, categories, domain rules, and focus enforcement.' },
  { id: 'agendaConfig', title: 'M.E.T.I.S', description: 'Configure nail timer, D.E.D.L.S templates, and Pomodoro.' },
  { id: 'accountConfig', title: 'Account', description: 'Manage your account and change your password.' },
];

export function SettingsSheet({ open, onClose, userId }: { open: boolean; onClose: () => void; userId: string | undefined }) {
  const styles = useStyles();
  const [categoryId, setCategoryId] = useState<string | null>(null);

  const handleClose = () => {
    setCategoryId(null);
    onClose();
  };

  const category = CATEGORIES.find((c) => c.id === categoryId) ?? null;

  return (
    <Modal visible={open} transparent animationType="slide" onRequestClose={handleClose}>
      <View style={styles.backdrop}>
        <Atmos style={styles.sheet}>
          <View style={styles.header}>
            {category ? (
              <Pressable onPress={() => setCategoryId(null)} style={styles.back}>
                <Text style={styles.backText}>‹ Settings</Text>
              </Pressable>
            ) : (
              <Text style={styles.title}>Settings</Text>
            )}
            <Pressable onPress={handleClose} style={styles.close}>
              <Text style={styles.closeText}>✕</Text>
            </Pressable>
          </View>

          {category ? (
            <CategoryDetail category={category} userId={userId} />
          ) : (
            <ScrollView contentContainerStyle={styles.content}>
              {CATEGORIES.map((c) => (
                <Pressable key={c.id} onPress={() => setCategoryId(c.id)}>
                  <GlassCard style={styles.categoryRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.categoryTitle}>{c.title}</Text>
                      <Text style={styles.categoryDesc}>{c.description}</Text>
                    </View>
                    <Text style={styles.categoryChevron}>›</Text>
                  </GlassCard>
                </Pressable>
              ))}
            </ScrollView>
          )}
        </Atmos>
      </View>
    </Modal>
  );
}

function CategoryDetail({ category, userId }: { category: SettingCategory; userId: string | undefined }) {
  const styles = useStyles();
  switch (category.id) {
    case 'generalConfig':
      return <GeneralSettings userId={userId} />;
    case 'olmpsConfig':
      return <OlmpsSettingsEditor userId={userId} />;
    case 'syqiConfig':
      return <SyqiDashboardEditor userId={userId} />;
    case 'trashConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <TrashEditor userId={userId} />
        </ScrollView>
      );
    case 'oracleConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <OracleEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>Drift rules</SectionTitle>
          <DriftRulesEditor userId={userId} />
        </ScrollView>
      );
    case 'accountConfig':
      return <AccountSettings />;
    case 'taskConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <SectionTitle style={styles.sectionGap}>Groups</SectionTitle>
          <GroupsEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>Values</SectionTitle>
          <ValuesEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>ETC Intervals</SectionTitle>
          <EtcIntervalsEditor userId={userId} />
        </ScrollView>
      );
    case 'calendarConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <CalendarEditor userId={userId} />
        </ScrollView>
      );
    case 'journalConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <JournalTypesEditor userId={userId} />
        </ScrollView>
      );
    case 'projectsConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <ProjectsEditor userId={userId} />
        </ScrollView>
      );
    case 'designConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <TkneEditor userId={userId} />
        </ScrollView>
      );
    case 'agendaConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <SectionTitle style={styles.sectionGap}>Nail timer & templates</SectionTitle>
          <MetisEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>Pomodoro</SectionTitle>
          <PomodoroEditor userId={userId} />
        </ScrollView>
      );
    case 'hrmezConfig':
      return <HrmezEditor userId={userId} />;
    case 'argosConfig':
      return <ArgosEditor userId={userId} />;
    case 'dashConfig':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <Text style={styles.categoryTitle}>{category.title}</Text>
          <Text style={styles.hint}>There are currently no settings for the dashboard.</Text>
        </ScrollView>
      );
    default:
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <Text style={styles.categoryTitle}>{category.title}</Text>
          <Text style={styles.hint}>Not available on mobile yet.</Text>
        </ScrollView>
      );
  }
}

function GeneralSettings({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { c } = useTheme();
  const { items: sidebarItems, setVisible, move } = useSidebarConfig(userId);
  const [notifStatus, setNotifStatus] = useState<string | null>(null);
  const [revealOn, setRevealOn] = useState(false);
  const [themeMode] = useThemeMode();
  const [themePref, setThemePref] = useThemePref();

  useEffect(() => {
    if (userId) void getRevealEnabled(userId).then(setRevealOn);
  }, [userId]);

  const testNotification = async () => {
    const result = await scheduleTestReminder();
    setNotifStatus(result);
  };

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <SectionTitle style={styles.sectionGap}>Appearance</SectionTitle>
      <View style={styles.sidebarRow}>
        <Text style={styles.sidebarLabel}>Theme · now {themeMode === 'dark' ? 'Night' : 'Day'}</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {(['dark', 'light', 'live'] as ThemePref[]).map((p) => (
            <Chip key={p} small label={p === 'dark' ? 'Night' : p === 'light' ? 'Day' : 'Live'} active={themePref === p} onPress={() => setThemePref(p)} />
          ))}
        </View>
      </View>

      <SectionTitle style={styles.sectionGap}>Sidebar</SectionTitle>
      <Text style={styles.hint}>Controls what shows in the menu drawer (top-left). Same list as web.</Text>
      {sidebarItems.length === 0 ? (
        <Text style={styles.hint}>Nothing configured yet.</Text>
      ) : (
        sidebarItems.map((item, idx) => (
          <View key={item.id} style={styles.sidebarRow}>
            <View style={styles.sidebarReorder}>
              <Pressable
                onPress={() => move(item.id, -1)}
                disabled={idx === 0}
                style={[styles.reorderBtn, idx === 0 && styles.reorderBtnDisabled]}
              >
                <Text style={styles.reorderText}>▲</Text>
              </Pressable>
              <Pressable
                onPress={() => move(item.id, 1)}
                disabled={idx === sidebarItems.length - 1}
                style={[styles.reorderBtn, idx === sidebarItems.length - 1 && styles.reorderBtnDisabled]}
              >
                <Text style={styles.reorderText}>▼</Text>
              </Pressable>
            </View>
            <Text style={styles.sidebarLabel}>{item.label}</Text>
            <ThemedSwitch
              value={item.visible}
              onValueChange={(v) => setVisible(item.id, v)}
              disabled={item.href === '/' || item.href === '/settings'}
            />
          </View>
        ))
      )}

      <SectionTitle style={styles.sectionGap}>Bottom nav</SectionTitle>
      <OrderedConfigEditor
        userId={userId}
        column="bottom_nav_tools"
        catalog={BOTTOM_NAV_CATALOG}
        defaults={DEFAULT_BOTTOM_NAV}
        label={bottomNavLabel}
        renderIcon={(id) => {
          const Icon = iconForTool(id);
          return <Icon size={18} color={c.slate} strokeWidth={2} />;
        }}
        maxVisible={MAX_BOTTOM_NAV}
        hint="Pick the tool icons for the bottom bar (between ORACLE and Settings)."
      />

      <SectionTitle style={styles.sectionGap}>Monday reveal</SectionTitle>
      <View style={styles.sidebarRow}>
        <Text style={styles.sidebarLabel}>Show "The week moved" on startup</Text>
        <ThemedSwitch
          value={revealOn}
          onValueChange={(v) => {
            setRevealOn(v);
            if (userId) void setRevealEnabled(userId, v);
          }}
        />
      </View>

      <SectionTitle style={styles.sectionGap}>Notifications</SectionTitle>
      <GoldButton ghost label="Send test reminder (fires in 10s)" onPress={() => void testNotification()} style={styles.actionGap} />
      {notifStatus ? <Text style={styles.hint}>{notifStatus}</Text> : null}
    </ScrollView>
  );
}

function AccountSettings() {
  const styles = useStyles();
  const { session, signOut } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) {
      Alert.alert('Passwords must match', 'Enter the same new password in both fields.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword('');
      setConfirmPassword('');
      Alert.alert('Password updated');
    } catch (e) {
      Alert.alert('Failed to update password', e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteAccount = () => {
    Alert.alert('Delete account?', 'This permanently deletes your account and data. This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete forever',
        style: 'destructive',
        onPress: async () => {
          try {
            await apiFetch('/api/mobile/delete-account', {
              method: 'POST',
              body: JSON.stringify({ confirm: 'DELETE' }),
            });
            await signOut();
          } catch (e) {
            Alert.alert('Deletion failed', e instanceof Error ? e.message : 'Try again or contact support.');
          }
        },
      },
    ]);
  };

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <SectionTitle style={styles.sectionGap}>Change Password</SectionTitle>
      <Text style={styles.hint}>Update the password for {session?.user.email}.</Text>
      <Field
        style={styles.fieldGap}
        placeholder="New password"
        secureTextEntry
        value={newPassword}
        onChangeText={setNewPassword}
      />
      <Field
        style={styles.fieldGap}
        placeholder="Confirm new password"
        secureTextEntry
        value={confirmPassword}
        onChangeText={setConfirmPassword}
      />
      <GoldButton
        label={saving ? 'Updating…' : 'Update Password'}
        onPress={() => {
          if (saving || !newPassword || !confirmPassword) return;
          void handleChangePassword();
        }}
        style={[styles.actionGap, (saving || !newPassword || !confirmPassword) && styles.actionButtonDisabled]}
      />

      <SectionTitle style={styles.sectionGap}>Account</SectionTitle>
      <GoldButton ghost label="Sign out" onPress={() => void signOut()} style={styles.actionGap} />
      <Pressable style={styles.dangerButton} onPress={confirmDeleteAccount}>
        <Text style={styles.dangerText}>Delete account</Text>
      </Pressable>
      <Text style={styles.hint}>
        Deleting your account permanently removes your login and data. This cannot be undone.
      </Text>
    </ScrollView>
  );
}

const useStyles = makeThemedStyles(({ t, c, radii }) =>
  StyleSheet.create({
  sectionGap: { marginTop: 18 },
  backdrop: { flex: 1, backgroundColor: c.overlay, justifyContent: 'flex-end' },
  sheet: {
    flex: 0,
    height: '88%',
    borderTopLeftRadius: radii.panel,
    borderTopRightRadius: radii.panel,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: t.navyLine,
    overflow: 'hidden',
    boxShadow: `0 -10px 30px ${t.glowSoft}`,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
  },
  title: { fontSize: 18, fontWeight: '900', letterSpacing: 1.5, color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  back: { position: 'absolute', left: 16, top: 12, padding: 4 },
  backText: { fontSize: 15, fontWeight: '700', color: t.goldBright },
  close: { position: 'absolute', right: 16, top: 14, padding: 4 },
  closeText: { fontSize: 18, color: t.ivorySoft },
  content: { paddingHorizontal: 16, paddingBottom: 48, gap: 8 },

  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: radii.tile,
    paddingVertical: 14,
    paddingHorizontal: 14,
    marginTop: 8,
  },
  categoryTitle: { fontSize: 15, fontWeight: '800', color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  categoryDesc: { fontSize: 12, color: t.ivorySoft, marginTop: 2 },
  categoryChevron: { fontSize: 20, fontWeight: '700', color: t.goldBright },

  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  fieldGap: { marginTop: 10 },
  actionGap: { marginTop: 10 },
  dangerButton: {
    marginTop: 14,
    borderRadius: radii.tile,
    borderWidth: 1,
    borderColor: t.lapsed,
    paddingVertical: 12,
    alignItems: 'center',
  },
  dangerText: { color: t.lapsed, fontWeight: '800' },
  actionButtonDisabled: { opacity: 0.5 },

  sidebarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginTop: 6,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  sidebarReorder: { gap: 2 },
  reorderBtn: {
    width: 26,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: t.lineFaint,
  },
  reorderBtnDisabled: { opacity: 0.25 },
  reorderText: { fontSize: 10, color: t.goldBright, fontWeight: '900' },
  sidebarLabel: { flex: 1, fontSize: 14, fontWeight: '700', color: t.ivory },
}),
);
